/**
 * Waits for a given number of milliseconds.
 * @param {number} ms - Milliseconds to wait
 * @returns {Promise<void>}
 */
export const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Retries a function with exponential backoff delay between attempts.
 * The delay increases quadratically with each attempt: baseDelayMs * attempt^2
 *
 * @param {object}   options
 * @param {Function} options.fn            - The async function to retry
 * @param {number}  [options.maxAttempts=3] - Maximum number of attempts (including the first)
 * @param {number}  [options.baseDelayMs=500] - Base delay in ms; grows as baseDelayMs * attempt²
 * @returns {Promise<any>} The result of fn() if any attempt succeeds
 * @throws {Error}  The last error if all attempts fail
 *
 * @example
 * const data = await retryWithExponentialBackoff({
 *   fn: () => ApiService.post('/v1/imei-reports/bulk-check', { imeis }),
 *   maxAttempts: 3,
 *   baseDelayMs: 500,
 * });
 */
export const retryWithExponentialBackoff = async ({
  fn,
  maxAttempts = 3,
  baseDelayMs = 500,
}) => {
  let attempt = 1;

  const execute = async () => {
    try {
      return await fn();
    } catch (error) {
      if (attempt >= maxAttempts) {
        throw error;
      }
      console.log('RETRY REQUEST ATTEMPT', attempt);
      const delayMs = baseDelayMs * Math.pow(attempt, 2);
      await wait(delayMs);
      attempt++;
      return execute();
    }
  };

  return execute();
};
